import { HeaderActions } from '@/features/nav/header-actions'
import type { AuthState, System } from '@/lib/auth-store'
import { cx } from '@/lib/cx'
import { useDocumentTitle } from '@/lib/document-title'
import { useT } from '@/lib/i18n/translate'

export interface TopBarProps<S extends System> {
  system: S
  auth: AuthState<S>
  /** The restaurant in scope (E6); the brand leads when there is none. */
  restaurant?: string | undefined
  /** The branch in scope (F4), on the second row. */
  branch?: string | undefined
  /** Second-row text after the branch: the Romi landing page's tagline. */
  detail?: string | undefined
  /** The page's name; leads the tab title, never shown in the bar. */
  page?: string | undefined
  /** Customer pages (G6): the bar is the page's own heading and centers with its body. */
  customer?: boolean
}

/**
 * The one header (T13, 2026-09-13): restaurant name over branch name on every page, app
 * and customer alike, then the four header actions. Without a restaurant the brand leads.
 */
export function TopBar<S extends System>({ system, auth, restaurant, branch, detail, page, customer = false }: TopBarProps<S>) {
  const t = useT()
  const brand = t(system === 'platform' ? 'app.platform' : 'app.name')
  const subtitle = [branch, detail].filter(Boolean).join(' · ')
  useDocumentTitle(page, branch, restaurant, brand)

  const titleClass = 'text-accent-ink truncate text-lg leading-tight font-semibold'
  return (
    <header
      className={cx('bg-surface border-border sticky top-0 z-30 h-14 border-b px-3 sm:px-6', !customer && 'print:hidden')}
    >
      <div className={cx('flex h-full items-center justify-between gap-3', customer && 'mx-auto max-w-2xl')}>
        <div className="flex min-w-0 flex-col">
          {customer ? (
            <h1 className={titleClass}>{restaurant ?? brand}</h1>
          ) : (
            <p className={titleClass}>{restaurant ?? brand}</p>
          )}
          {subtitle && <p className="text-muted truncate text-sm leading-tight">{subtitle}</p>}
        </div>
        <HeaderActions system={system} auth={auth} />
      </div>
    </header>
  )
}
